import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { useNumber } from 'react-use';

import { fetchFiltersOptions as fetchFiltersOptionsAction } from '@thunks/filters';
import { fetchProducts } from '@thunks/products';

import { prepareProductsRequestParams } from '@utils/filters';
import { getRouteUrl } from '@utils/urls';

import { OFFER_TYPES } from '@common/constants/filters';
import Layout from '@common/containers/Layout';
import ProductsList from '@common/components/ProductsList';
import useIsomorphicLayoutEffect from '@common/hooks/useIsomorphicLayoutEffect';
import { FiltersProvider } from '@common/components/Filters';

export function AllProducts({
  offerType,
  newArrivalOnly,
  publishedOnly,
  metaTitle,
  pageTitle,
  pageUrl,
}) {
  const {
    asPath,
    query: { page: queryPage, ...query },
    replace: routerReplace,
    locale,
  } = useRouter();
  const page = parseInt(queryPage || 1, 10) || 1;
  const [startPage, setStartPage] = useState(page);
  const dispatch = useDispatch();

  const [
    lastFetchedPage,
    { inc: incLastFetchedPage, reset: resetLastFetchedPage },
  ] = useNumber(page);
  const pathWithoutPage = asPath.replace(/[?&]page=\d+/, '');

  useIsomorphicLayoutEffect(() => {
    if ((startPage === lastFetchedPage && !queryPage) || queryPage) {
      return;
    }

    resetLastFetchedPage(1);
    setStartPage(1);
  }, [pathWithoutPage, queryPage]);

  const fetchNextPage = () => {
    const productsParams = prepareProductsRequestParams(
      {
        page: lastFetchedPage + 1,
        ...query,
      },
      {
        newArrivalOnly,
        offerType,
        publishedOnly,
      },
    );

    incLastFetchedPage();

    dispatch(fetchProducts(productsParams));
  };

  const pageAsUrl = getRouteUrl(locale, pageUrl);

  const [currentlyViewedPage, setCurrentlyViewedPage] = useState(page);

  useIsomorphicLayoutEffect(() => {
    setCurrentlyViewedPage(page);
  }, [offerType]);

  const changePage = (toPage, byScroll = false) => {
    const searchParamsFromUrl = asPath.split('?')[1];
    const queryString = new URLSearchParams(
      prepareProductsRequestParams(
        {
          page: toPage > 1 ? toPage : null,
          ...query,
        },
        Object.fromEntries(new URLSearchParams(searchParamsFromUrl).entries()),
      ),
    )
      .toString()
      .replace(/^./, '?$&');

    routerReplace(
      {
        pathname: pageUrl,
        query: {
          page: toPage,
          ...query,
        },
      },
      `${pageAsUrl}${queryString}`,
      byScroll
        ? {
            shallow: true,
            scroll: false,
          }
        : {
            shallow: false,
          },
    ).then(() => {
      if (!byScroll) {
        window.scrollTo(0, 0);
        resetLastFetchedPage(toPage);
        setStartPage(toPage);
      }
    });
  };

  useEffect(() => {
    changePage(currentlyViewedPage, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentlyViewedPage, pageUrl]);

  const fetchFiltersOptions = useCallback(
    (additionalParams, options) => {
      dispatch(
        fetchFiltersOptionsAction(
          {
            ...additionalParams,
            newArrivalOnly,
            offerType,
            publishedOnly,
          },
          options,
        ),
      ).finally(() => {
        if (options?.callback) {
          options.callback();
        }
      });
    },
    [dispatch, newArrivalOnly, offerType, publishedOnly],
  );

  useEffect(() => {
    fetchFiltersOptions({}, { isInitial: true });
  }, [fetchFiltersOptions]);

  return (
    <Layout title={metaTitle} canonicalPageUrl={`/${locale}${asPath}`}>
      <FiltersProvider>
        <ProductsList
          title={pageTitle}
          offerType={offerType}
          newArrivalOnly={newArrivalOnly}
          publishedOnly={publishedOnly}
          startPage={startPage}
          setCurrentlyViewedPage={setCurrentlyViewedPage}
          fetchNextPage={fetchNextPage}
          fetchFiltersOptions={fetchFiltersOptions}
          onPageChange={changePage}
        />
      </FiltersProvider>
    </Layout>
  );
}

AllProducts.propTypes = {
  newArrivalOnly: PropTypes.bool,
  offerType: PropTypes.oneOf(Object.keys(OFFER_TYPES)),
  publishedOnly: PropTypes.bool,
  metaTitle: PropTypes.string.isRequired,
  pageTitle: PropTypes.string.isRequired,
  pageUrl: PropTypes.string.isRequired,
};

AllProducts.defaultProps = {
  newArrivalOnly: false,
  offerType: undefined,
  publishedOnly: false,
};

export default AllProducts;
